import { useMemo } from 'react';
import {
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import nlLocale from '@fullcalendar/core/locales/nl';
import { T, stitchTooltipSlotProps } from './stitch';
import { formatDateISO } from '../../../utils/weekUtils';
import { crewSamengevoegd } from '../../../utils/draaidagCrew';
import { dagErna, draaidagPeriode } from '../../../utils/nlDatum';


// Draaidagen Calendar Component (FullCalendar)
function DraaidagenCalendar({ draaidagen }) {
  const events = useMemo(() => {
    return draaidagen.map(dd => {
      // Pure NL-dagen, einde inclusief. De Outlook-tijden zijn UTC met een exclusief
      // einde: een hele dag op 24 nov besloeg hier 23 én 24 nov (23 okt 2026).
      const { start: startDateStr, eind: endDateStr } = draaidagPeriode(dd.fields);
      const name = dd.fields?.Draaidag || 'Draaidag';
      const crew = dd.fields?.['Naam (from Crew)'] || [];
      const crewArray = crewSamengevoegd(crew, name).alle;
      const planningstatus = dd.fields?.Planningstatus || '';

      const parts = name.split('||');
      const crewPart = parts[0]?.trim() || '';
      const projectPart = parts[1]?.trim() || name;

      let backgroundColor = T.emerald;
      if (planningstatus.includes('OPTIE') || planningstatus.includes('mogelijk')) {
        backgroundColor = T.amber;
      } else if (planningstatus.includes('OPEN')) {
        backgroundColor = T.error;
      }

      // FullCalendar wil een exclusief einde: de dag ná de laatste dag.
      const endDate = endDateStr ? dagErna(endDateStr) : null;

      return {
        id: dd.id,
        title: crewPart || projectPart,
        start: startDateStr,
        end: endDate,
        allDay: true,
        backgroundColor,
        borderColor: backgroundColor,
        extendedProps: { crew: crewArray, status: planningstatus, fullName: name, pl: dd.fields?.PL || '' },
      };
    }).filter(e => e.start);
  }, [draaidagen]);

  const defaultDate = useMemo(() => {
    if (events.length > 0) {
      const sorted = [...events].sort((a, b) => new Date(a.start) - new Date(b.start));
      return sorted[0].start;
    }
    return formatDateISO(new Date());
  }, [events]);

  return (
    <Box sx={{ p: 2, bgcolor: T.surface, boxShadow: T.shadow, borderRadius: '0.75rem' }}>
      <Box sx={{
        '& .fc': { fontFamily: T.fontBody, fontSize: '0.82rem' },
        '& .fc-toolbar-title': { fontSize: '1.1rem !important', fontFamily: T.font, fontWeight: 700 },
        '& .fc-button-primary': {
          backgroundColor: `${T.surface} !important`, borderColor: `${T.surfaceHigh} !important`,
          color: `${T.text} !important`, fontSize: '0.78rem !important', fontFamily: `${T.fontBody} !important`,
          '&:hover': { backgroundColor: `${T.surfaceLow} !important` },
          '&.fc-button-active': { backgroundColor: `${T.primary} !important`, borderColor: `${T.primary} !important`, color: '#fff !important' },
        },
        '& .fc-day-today': { backgroundColor: `${T.primaryContainer} !important` },
        '& .fc-daygrid-day-number': { fontSize: '0.78rem', fontWeight: 500, padding: '4px 8px' },
        '& .fc-event': { borderRadius: '4px !important', fontSize: '0.72rem !important', padding: '1px 4px !important', cursor: 'pointer', border: 'none !important' },
        '& .fc-daygrid-event-dot': { display: 'none' },
        '& .fc-col-header-cell-cushion': { fontWeight: 600, fontSize: '0.75rem', color: T.outline },
        '& .fc-scrollgrid': { borderColor: T.surfaceHigh },
        '& .fc-theme-standard td, & .fc-theme-standard th': { borderColor: T.surfaceLow },
        '& .fc-daygrid-more-link': { fontSize: '0.65rem', color: T.primary, fontWeight: 600 },
        '& .fc-list-event-title': { fontSize: '0.82rem' },
        '& .fc-list-day-cushion': { fontWeight: 600, fontSize: '0.82rem' },
      }}>
        <FullCalendar
          plugins={[dayGridPlugin, listPlugin]}
          initialView="dayGridMonth"
          initialDate={defaultDate}
          locale={nlLocale}
          headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,dayGridWeek,listMonth' }}
          buttonText={{ today: 'Vandaag', month: 'Maand', week: 'Week', list: 'Lijst' }}
          events={events}
          dayMaxEvents={4}
          moreLinkText={(n) => `+${n} meer`}
          firstDay={1}
          height="auto"
          eventContent={(arg) => {
            const props = arg.event.extendedProps;
            return (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600 }}>{props.fullName}</Typography>
                    {props.crew?.length > 0 && <Typography variant="caption" sx={{ fontFamily: T.fontBody }} display="block">Crew: {props.crew.join(', ')}</Typography>}
                    {props.pl && <Typography variant="caption" sx={{ fontFamily: T.fontBody }} display="block">PL: {props.pl}</Typography>}
                  </Box>
                }
                arrow
                slotProps={stitchTooltipSlotProps}
              >
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {arg.event.title}
                </Box>
              </Tooltip>
            );
          }}
        />
      </Box>
    </Box>
  );
}

export default DraaidagenCalendar;
